import React, { useMemo } from 'react';
import { Box, Chip, Divider, FormControl, InputLabel, ListSubheader, MenuItem, Select } from '@mui/material';
import { AUTHOR_ALL } from './usePrPageUrlState';

interface AuthorFilterProps {
  authors: string[];                     // distinct author logins across open PRs
  prCountByAuthor: Map<string, number>;  // open PR count per login, for the badges
  value: string;                         // current selection (login or AUTHOR_ALL)
  myLogin?: string;                      // the signed-in user's GitHub login, surfaced at the top
  onChange: (author: string) => void;
}

// Open PR count shown to the right of an option's name, tallied from the PRs the page already has
// loaded — no extra query. Omitted at zero so an author restored from a URL but with nothing open
// doesn't get a "0" badge.
const CountBadge: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    // ml:auto flushes the badge to the right edge of the flex MenuItem. The closed Select renders
    // the selected item's children in a plain block where that margin collapses, so pl supplies the
    // gap from the name there.
    <Box component="span" sx={{ ml: 'auto', pl: 0.75 }}>
      <Chip size="small" variant="outlined" label={count} sx={{ height: 18, fontSize: 11 }} />
    </Box>
  ) : null;

// The author dropdown. Order: "Everyone", then me, a divider, then everyone else alphabetically.
// The current value is always selectable even if that author has no open PRs right now (e.g.
// restored from a shared URL).
const AuthorFilter: React.FC<AuthorFilterProps> = ({ authors, prCountByAuthor, value, myLogin, onChange }) => {
  const others = useMemo(
    () => authors.filter(a => a !== myLogin).sort((a, b) => a.localeCompare(b)),
    [authors, myLogin]
  );

  const totalPrs = useMemo(
    () => Array.from(prCountByAuthor.values()).reduce((sum, n) => sum + n, 0),
    [prCountByAuthor]
  );

  // MUI needs the current value to correspond to a rendered MenuItem, so include it if it's an
  // author that isn't otherwise in the lists.
  const valueMissing =
    value !== AUTHOR_ALL && value !== myLogin && !others.includes(value);

  const items: React.ReactNode[] = [
    <MenuItem key="__all" value={AUTHOR_ALL}>Everyone<CountBadge count={totalPrs} /></MenuItem>,
  ];
  if (myLogin) {
    items.push(
      <MenuItem key={myLogin} value={myLogin}>
        {myLogin} (me)<CountBadge count={prCountByAuthor.get(myLogin) ?? 0} />
      </MenuItem>
    );
  }
  if (valueMissing) {
    items.push(<MenuItem key={value} value={value}>{value}</MenuItem>);
  }
  if (others.length > 0) {
    items.push(<Divider key="__divider" component="li" />);
    items.push(<ListSubheader key="__others" disableSticky>Other authors</ListSubheader>);
    others.forEach(a =>
      items.push(
        <MenuItem key={a} value={a}>
          {a}<CountBadge count={prCountByAuthor.get(a) ?? 0} />
        </MenuItem>
      )
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="pr-author-filter-label">Show changes from</InputLabel>
      <Select
        labelId="pr-author-filter-label"
        label="Show changes from"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {items}
      </Select>
    </FormControl>
  );
};

export default AuthorFilter;
